import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouterProviderProps } from 'react-router-dom';

import { PrivateRoute } from './components/PrivateRoute.js';
import { RootLayout } from './components/RootLayout.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';

const SystemMapPage = lazy(async () => {
  const mod = await import('./pages/dev/SystemMapPage.js');
  return { default: mod.default };
});

const EmployeesPage = lazy(async () => {
  const mod = await import('./pages/employees/EmployeesPage.js');
  return { default: mod.EmployeesPage };
});

const EmployeeDetailPage = lazy(async () => {
  const mod = await import('./pages/employees/EmployeeDetailPage.js');
  return { default: mod.EmployeeDetailPage };
});

const EmployeeProfilePage = lazy(async () => {
  const mod = await import('./pages/employees/EmployeeProfilePage.js');
  return { default: mod.EmployeeProfilePage };
});

const LeavePage = lazy(async () => {
  const mod = await import('./pages/leave/LeavePage.js');
  return { default: mod.LeavePage };
});

const AttendancePage = lazy(async () => {
  const mod = await import('./pages/attendance/AttendancePage.js');
  return { default: mod.AttendancePage };
});

const PayrollPage = lazy(async () => {
  const mod = await import('./pages/payroll/PayrollPage.js');
  return { default: mod.PayrollPage };
});

const PayslipDetailPage = lazy(async () => {
  const mod = await import('./pages/payroll/PayslipDetailPage.js');
  return { default: mod.PayslipDetailPage };
});

const PerformancePage = lazy(async () => {
  const mod = await import('./pages/performance/PerformancePage.js');
  return { default: mod.PerformancePage };
});

const RecruitmentPage = lazy(async () => {
  const mod = await import('./pages/recruitment/RecruitmentPage.js');
  return { default: mod.RecruitmentPage };
});

const CareersPageBuilder = lazy(async () => {
  const mod = await import('./pages/recruitment/CareersPageBuilder.js');
  return { default: mod.default };
});

const ExpensesPage = lazy(async () => {
  const mod = await import('./pages/expenses/ExpensesPage.js');
  return { default: mod.ExpensesPage };
});

const AssetsPage = lazy(async () => {
  const mod = await import('./pages/assets/AssetsPage.js');
  return { default: mod.AssetsPage };
});

const HelpdeskPage = lazy(async () => {
  const mod = await import('./pages/helpdesk/HelpdeskPage.js');
  return { default: mod.HelpdeskPage };
});

const OnboardingPage = lazy(async () => {
  const mod = await import('./pages/onboarding/OnboardingPage.js');
  return { default: mod.OnboardingPage };
});

const ExitPage = lazy(async () => {
  const mod = await import('./pages/exit/ExitPage.js');
  return { default: mod.ExitPage };
});

const EngagementPage = lazy(async () => {
  const mod = await import('./pages/engagement/EngagementPage.js');
  return { default: mod.EngagementPage };
});

const AgencyPage = lazy(async () => {
  const mod = await import('./pages/agency/AgencyPage.js');
  return { default: mod.AgencyPage };
});

const TrainingPage = lazy(async () => {
  const mod = await import('./pages/training/TrainingPage.js');
  return { default: mod.TrainingPage };
});

const VendorsPage = lazy(async () => {
  const mod = await import('./pages/vendors/VendorsPage.js');
  return { default: mod.VendorsPage };
});

const CompliancePage = lazy(async () => {
  const mod = await import('./pages/compliance/CompliancePage.js');
  return { default: mod.CompliancePage };
});

const WhistleblowerPage = lazy(async () => {
  const mod = await import('./pages/whistleblower/WhistleblowerPage.js');
  return { default: mod.WhistleblowerPage };
});

const WorkforcePage = lazy(async () => {
  const mod = await import('./pages/workforce/WorkforcePage.js');
  return { default: mod.WorkforcePage };
});

const DeiPage = lazy(async () => {
  const mod = await import('./pages/dei/DeiPage.js');
  return { default: mod.DeiPage };
});

const DocumentsPage = lazy(async () => {
  const mod = await import('./pages/documents/DocumentsPage.js');
  return { default: mod.DocumentsPage };
});

const SubscriptionPage = lazy(async () => {
  const mod = await import('./pages/subscription/SubscriptionPage.js');
  return { default: mod.SubscriptionPage };
});

const LifecyclePage = lazy(async () => {
  const mod = await import('./pages/lifecycle/LifecyclePage.js');
  return { default: mod.LifecyclePage };
});

const SuperAdminPage = lazy(async () => {
  const mod = await import('./pages/admin/SuperAdminPage.js');
  return { default: mod.SuperAdminPage };
});

const RolesPage = lazy(async () => {
  const mod = await import('./pages/admin/RolesPage.js');
  return { default: mod.RolesPage };
});

const ReportsPage = lazy(async () => {
  const mod = await import('./pages/reports/ReportsPage.js');
  return { default: mod.ReportsPage };
});

const OrgPage = lazy(async () => {
  const mod = await import('./pages/org/OrgPage.js');
  return { default: mod.OrgPage };
});

const ESSPage = lazy(async () => {
  const mod = await import('./pages/ess/ESSPage.js');
  return { default: mod.ESSPage };
});

const DashboardPage = lazy(async () => {
  const mod = await import('./pages/dashboard/DashboardPage.js');
  return { default: mod.DashboardPage };
});

const AICopilotPage = lazy(async () => {
  const mod = await import('./pages/ai/AICopilotPage.js');
  return { default: mod.AICopilotPage };
});

const CompensationPage = lazy(async () => {
  const mod = await import('./pages/compensation/CompensationPage.js');
  return { default: mod.CompensationPage };
});

const CompanySettingsPage = lazy(async () => {
  const mod = await import('./pages/settings/CompanySettingsPage.js');
  return { default: mod.CompanySettingsPage };
});

const ContractsPage = lazy(async () => {
  const mod = await import('./pages/contracts/ContractsPage.js');
  return { default: mod.ContractsPage };
});

const NotificationsPage = lazy(async () => {
  const mod = await import('./pages/notifications/NotificationsPage.js');
  return { default: mod.NotificationsPage };
});

const OmbudsmanPage = lazy(async () => import('./pages/public/OmbudsmanPage.js'));

const CareersPage = lazy(async () => import('./pages/public/CareersPage.js'));

function PageFallback(): ReactNode {
  return (
    <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center text-sm">
      Loading…
    </div>
  );
}

export const router: RouterProviderProps['router'] = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      {
        path: 'report/:tenantSlug',
        element: (
          <Suspense fallback={<PageFallback />}>
            <OmbudsmanPage />
          </Suspense>
        ),
      },
      {
        path: 'careers/:tenantSlug',
        element: (
          <Suspense fallback={<PageFallback />}>
            <CareersPage />
          </Suspense>
        ),
      },
      {
        path: 'dev/system-map',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SystemMapPage />
          </Suspense>
        ),
      },
      {
        element: <PrivateRoute />,
        children: [
          {
            path: 'dashboard',
            element: (
              <Suspense fallback={<PageFallback />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: 'ess',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ESSPage />
              </Suspense>
            ),
          },
          {
            path: 'employees',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EmployeesPage />
              </Suspense>
            ),
          },
          {
            path: 'employees/:id',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EmployeeDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'employees/:id/profile',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EmployeeProfilePage />
              </Suspense>
            ),
          },
          {
            path: 'org',
            element: (
              <Suspense fallback={<PageFallback />}>
                <OrgPage />
              </Suspense>
            ),
          },
          {
            path: 'leave',
            element: (
              <Suspense fallback={<PageFallback />}>
                <LeavePage />
              </Suspense>
            ),
          },
          {
            path: 'attendance',
            element: (
              <Suspense fallback={<PageFallback />}>
                <AttendancePage />
              </Suspense>
            ),
          },
          {
            path: 'payroll',
            element: (
              <Suspense fallback={<PageFallback />}>
                <PayrollPage />
              </Suspense>
            ),
          },
          {
            path: 'payroll/payslips/:id',
            element: (
              <Suspense fallback={<PageFallback />}>
                <PayslipDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'performance',
            element: (
              <Suspense fallback={<PageFallback />}>
                <PerformancePage />
              </Suspense>
            ),
          },
          {
            path: 'recruitment',
            element: (
              <Suspense fallback={<PageFallback />}>
                <RecruitmentPage />
              </Suspense>
            ),
          },
          {
            path: 'recruitment/careers-page',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CareersPageBuilder />
              </Suspense>
            ),
          },
          {
            path: 'expenses',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ExpensesPage />
              </Suspense>
            ),
          },
          {
            path: 'assets',
            element: (
              <Suspense fallback={<PageFallback />}>
                <AssetsPage />
              </Suspense>
            ),
          },
          {
            path: 'helpdesk',
            element: (
              <Suspense fallback={<PageFallback />}>
                <HelpdeskPage />
              </Suspense>
            ),
          },
          {
            path: 'onboarding',
            element: (
              <Suspense fallback={<PageFallback />}>
                <OnboardingPage />
              </Suspense>
            ),
          },
          {
            path: 'exit',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ExitPage />
              </Suspense>
            ),
          },
          {
            path: 'engagement',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EngagementPage />
              </Suspense>
            ),
          },
          {
            path: 'agency',
            element: (
              <Suspense fallback={<PageFallback />}>
                <AgencyPage />
              </Suspense>
            ),
          },
          {
            path: 'training',
            element: (
              <Suspense fallback={<PageFallback />}>
                <TrainingPage />
              </Suspense>
            ),
          },
          {
            path: 'vendors',
            element: (
              <Suspense fallback={<PageFallback />}>
                <VendorsPage />
              </Suspense>
            ),
          },
          {
            path: 'compliance',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CompliancePage />
              </Suspense>
            ),
          },
          {
            path: 'whistleblower',
            element: (
              <Suspense fallback={<PageFallback />}>
                <WhistleblowerPage />
              </Suspense>
            ),
          },
          {
            path: 'workforce',
            element: (
              <Suspense fallback={<PageFallback />}>
                <WorkforcePage />
              </Suspense>
            ),
          },
          {
            path: 'dei',
            element: (
              <Suspense fallback={<PageFallback />}>
                <DeiPage />
              </Suspense>
            ),
          },
          {
            path: 'documents',
            element: (
              <Suspense fallback={<PageFallback />}>
                <DocumentsPage />
              </Suspense>
            ),
          },
          {
            path: 'subscription',
            element: (
              <Suspense fallback={<PageFallback />}>
                <SubscriptionPage />
              </Suspense>
            ),
          },
          {
            path: 'lifecycle',
            element: (
              <Suspense fallback={<PageFallback />}>
                <LifecyclePage />
              </Suspense>
            ),
          },
          {
            path: 'admin',
            element: (
              <Suspense fallback={<PageFallback />}>
                <SuperAdminPage />
              </Suspense>
            ),
          },
          {
            path: 'admin/roles',
            element: (
              <Suspense fallback={<PageFallback />}>
                <RolesPage />
              </Suspense>
            ),
          },
          {
            path: 'reports',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ReportsPage />
              </Suspense>
            ),
          },
          {
            path: 'ai-copilot',
            element: (
              <Suspense fallback={<PageFallback />}>
                <AICopilotPage />
              </Suspense>
            ),
          },
          {
            path: 'compensation',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CompensationPage />
              </Suspense>
            ),
          },
          {
            path: 'settings/company',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CompanySettingsPage />
              </Suspense>
            ),
          },
          {
            path: 'contracts',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ContractsPage />
              </Suspense>
            ),
          },
          {
            path: 'notifications',
            element: (
              <Suspense fallback={<PageFallback />}>
                <NotificationsPage />
              </Suspense>
            ),
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
